import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LoginForm } from '@/components/auth/LoginForm';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { usePublicBranding } from '@/hooks/useSettings';
import { Skeleton } from '@/components/ui/skeleton';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('auth');
  const { data: branding, isLoading: isLoadingBranding } = usePublicBranding();
  const [logoError, setLogoError] = useState(false);

  // Get the intended destination from location state
  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard';

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const appName = branding?.app_name || 'Antenneservice';
  const logoUrl = branding?.logo_url;
  const showLogo = logoUrl && !logoError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <Card className="w-full max-w-md rounded-[14px] border-border-c shadow-card-lift">
        <CardHeader className="space-y-4 text-center">
          {/* Company Logo */}
          {isLoadingBranding ? (
            <div className="flex flex-col items-center gap-4">
              <Skeleton className="h-16 w-48" />
              <Skeleton className="h-6 w-32" />
            </div>
          ) : (
            <>
              {showLogo ? (
                <div className="flex justify-center">
                  <img
                    src={logoUrl}
                    alt={appName}
                    className="h-16 w-auto max-w-[240px] object-contain"
                    onError={() => setLogoError(true)}
                  />
                </div>
              ) : (
                <div className="flex justify-center">
                  <div className="grid h-14 w-14 place-items-center rounded-xl bg-accent-soft text-accent-c border border-border-c">
                    <span className="font-brand text-[20px] font-extrabold">
                      {appName.slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                </div>
              )}

              {/* Company Name / App Name */}
              <CardTitle className="font-brand text-[22px] font-extrabold tracking-[-0.015em] text-ink">
                {appName}
              </CardTitle>
            </>
          )}

          {/* Login Subtitle */}
          <CardDescription className="text-[12.5px] text-muted-foreground">
            {t('login.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <LoginForm redirectTo={from} />
        </CardContent>
      </Card>
    </div>
  );
}
